import { CrudFilters, DataProvider } from "@refinedev/core";
import { stringify } from "@refinedev/simple-rest";
import { AxiosInstance } from "axios";
import {
  handleCreateData,
  handleDeleteData,
  handleGetData,
  handleUpdateData,
  isAnalyticsRequest,
  SettingDebugInfo,
} from "pages/analyticsWatchLists/utils";
import { isBadWordRequest } from "pages/badwords/utils";
import { axiosInstance, generateFilter, generateSort } from "./utils";
import {
  handleAudioUpload,
  handleCommonCreateRequest,
  handleCommonDeleteRequest,
  handleCommonFindRequest,
  handleCommonGetRequest,
  handleCommonPatchRequest,
  handleHpa,
  handleSettingPatch,
} from "./utils/tools";
import { PaginationTypes, TableNameType } from "./utils/types";

const isCommonReasons = (r: string): boolean => r === "common-reasons";
const isTicket = (r: string): boolean => r === "tickets";
const isSettings = (r: string): boolean => r === "settings";
const isFile = (r: string): boolean => r === "file";
const isReview = (r: string): boolean => r === "review";
const isReportedMessage = (r: string): boolean => r === "reported-messages";
const isMessageIncidents = (r: string): boolean => r === "message-incidents";

const CommonFindResources = [
  "rooms",
  "prod-categories",
  "stream-vendors",
  "prod-category-room-relations",
  "prod-category-badge-relations",
  "common-reasons",
  "tickets",
  "user-badge-relations",
  "quotestream-packages",
  "prod-category-quotestream-package-relations",
  "user-badges",
  "orca-deploy-activity-logs",
];

const CommonCreateResources = [
  "prod-category-room-relations",
  "prod-category-badge-relations",
  "prod-category-quotestream-package-relations",
  "orca-deploy-activity-logs",
];

const CommonDeleteResources = [
  "prod-categories",
  "prod-category-room-relations",
  "prod-category-badge-relations",
  "prod-category-quotestream-package-relations",
];

const resourceMap: any = {
  "product-categories": "prod-categories",
};

const handleFilters = (filters: CrudFilters) => {
  filters = filters.filter((e: any) => {
    const isStatus = ["activeStatus", "status"].includes(e.field);
    const isAll = e.value == 2;
    return !(isStatus && isAll);
  });
  return filters;
};

export const dataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): Omit<
  Required<DataProvider>,
  "createMany" | "updateMany" | "deleteMany"
> => ({
  getList: async ({ resource: origin, pagination, filters, sorters }) => {
    const resource = resourceMap[origin] || origin;
    filters = filters && handleFilters(filters);
    const { current, pageSize, mode = "server" } = pagination ?? {};
    const queryFilters = generateFilter(filters);
    const generatedSort = generateSort(sorters);
    let query: string = "";
    let paginationData: PaginationTypes = {
      $limit: pageSize,
      $skip: 0,
    };
    if (mode === "server" && current !== undefined && pageSize !== undefined) {
      query = `$limit=${pageSize}&$skip=${(current - 1) * pageSize}`;
      paginationData = {
        $limit: pageSize,
        $skip: (current - 1) * pageSize,
      };
    }
    if (queryFilters) {
      query = `${query}&${stringify(queryFilters)}`;
    }
    if (generatedSort) {
      query = `${query}&${generatedSort}`;
    }

    if (CommonFindResources.includes(resource)) {
      const data = await handleCommonFindRequest(
        resource as TableNameType,
        filters,
        sorters,
        paginationData
      );
      return data;
    }
    if (resource === "badge") {
      const data = await handleCommonFindRequest(
        "badges",
        filters,
        sorters,
        paginationData
      );
      return data;
    }

    if (resource === "badge-type") {
      const data = await handleCommonFindRequest(
        "badge-types",
        filters,
        sorters,
        paginationData
      );
      return data;
    }

    if (resource === "followers-settings") {
      const data = await handleCommonFindRequest(
        "badge-rules",
        filters,
        sorters,
        paginationData
      );
      return data;
    }
    if (resource === "role-badges-assignments") {
      const data = await handleCommonFindRequest(
        "role-badge-relations",
        filters,
        sorters,
        paginationData
      );
      return data;
    }
    if (resource === "user") {
      const data = await handleCommonFindRequest(
        "users",
        filters,
        sorters,
        paginationData
      );
      return data;
    }
    if (resource === "room-member") {
      const data = await handleCommonFindRequest(
        "room-members",
        filters,
        sorters,
        paginationData
      );
      return data;
    }

    if (isAnalyticsRequest(resource)) {
      const data = await handleCommonFindRequest("settings", [
        {
          field: "$or[0][name]",
          value: "analyticsWatchList",
          operator: "eq",
        },
      ]);
      return handleGetData(data, query);
    }

    if (isBadWordRequest(resource)) {
      const data = await handleCommonFindRequest(
        "bad-words",
        filters,
        sorters,
        paginationData,
        "message"
      );
      return data;
    }

    if (isSettings(resource)) {
      const data = await handleCommonFindRequest("settings", undefined, [
        { field: "id", order: "asc" },
      ]);
      const arr = ["popoutCloseWhiteList", "analyticsWatchList"];
      const res = data.data.filter((item: any) => !arr.includes(item.name));
      return { data: res };
    }

    if (isReview(resource)) {
      const data = await handleCommonFindRequest(
        "uri-reviews",
        filters,
        sorters,
        paginationData,
        "message"
      );
      return data;
    }

    if (isReportedMessage(resource)) {
      const data = await handleCommonFindRequest(
        "api/v2/reported-messages",
        filters,
        sorters,
        paginationData,
        "message"
      );
      return data;
    }

    if (isMessageIncidents(resource)) {
      const data = await handleCommonFindRequest(
        "message-incidents",
        filters,
        sorters,
        paginationData,
        "message"
      );
      return data;
    }

    if (resource === "user/profile") {
      const data = await handleCommonFindRequest(
        "users",
        filters,
        sorters,
        paginationData
      );
      return data;
    }

    const url = `${apiUrl}/${resource}`;

    // scan server
    if (resource.endsWith("scanner/config")) {
      const { data } = await httpClient.get(url);
      return { data: data, total: data?.total };
    }
    // scan server
    if (resource === "symbols") {
      const { data } = await httpClient.get(`${url}/forward?${query}`);
      return { data: data.data, total: data.total };
    }

    if (resource === "scanner-config-changes" && current && pageSize) {
      const { data } = await httpClient.get(
        `${apiUrl}/v1/scanner/config-changes?limit=${pageSize}&skip=${
          (current - 1) * pageSize
        }`
      );
      return { data: data.data, total: data.total };
    }

    const { data } = await httpClient.get(`${url}?${query}`);
    return { data: data.data, total: data.total };
  },

  getMany: async ({ resource, ids }) => {
    if (resource === "scanner-config-changes") {
      const { data } = await httpClient.get(
        `${apiUrl}/v1/scanner/config-changes?${stringify({
          widget_id: ids[0],
        })}`
      );
      return { data: data.data, total: data.total };
    }
    const { data } = await httpClient.get(
      `${apiUrl}/${resource}?${stringify({ id: ids })}`
    );
    return {
      data,
    };
  },

  create: async ({ resource: origin, variables }) => {
    const resource = resourceMap[origin] || origin;
    const {
      config,
      name,
      playerParams,
      pusherParams,
      showName,
      title,
      sourceType,
      avatarUrl,
    } = variables as Record<string, string>;
    let url = `${apiUrl}/${resource}`;
    if (isAnalyticsRequest(resource)) {
      const data = handleCreateData(variables, SettingDebugInfo);
      const res = await handleSettingPatch(SettingDebugInfo.id, data);
      return res;
    }
    if (resource === "symbols") {
      url = `${apiUrl}/${resource}/forward`;
      variables = {
        ...variables,
        source: "admin",
      };
    }
    if (resource === "user") {
      url = `${apiUrl}/v2/admin/${resource}/account`;
    }
    if (resource === "quotestream-packages") {
      const key = name.replace(/\s+/g, "_").toUpperCase();
      return handleCommonCreateRequest("quotestream-packages", {
        ...variables,
        key,
      });
    }
    if (CommonCreateResources.includes(resource)) {
      return handleCommonCreateRequest(resource, variables);
    }
    if (isCommonReasons(resource)) {
      variables = { ...variables, admin: "true" };
      return handleCommonCreateRequest("common-reasons", variables);
    }
    if (resource === "followers-settings") {
      return handleCommonCreateRequest("badge-rules", variables);
    }
    if (isBadWordRequest(resource)) {
      variables = { ...variables, source: 1 };
      return handleCommonCreateRequest("bad-words", variables, "message");
    }
    if (resource === "stream-vendors") {
      const isJSON = (str: any) => {
        if (typeof str == "string") {
          try {
            JSON.parse(str);
            return true;
          } catch (e) {
            return false;
          }
        } else {
          return false;
        }
      };
      variables = {
        ...variables,
        config: isJSON(config) ? JSON.parse(config) : config,
        name,
        playerParams: isJSON(playerParams)
          ? JSON.parse(playerParams)
          : playerParams,
        pusherParams: isJSON(pusherParams)
          ? JSON.parse(pusherParams)
          : pusherParams,
        showName,
      };
      return handleCommonCreateRequest("stream-vendors", variables);
    }
    if (resource === "badge") {
      return handleCommonCreateRequest("badges", variables);
    }
    if (resource === "badge-type") {
      return handleCommonCreateRequest("badge-types", variables);
    }
    if (resource === "role-badges-assignments") {
      return handleCommonCreateRequest("role-badge-relations", variables);
    }
    if (resource === "user-badge-relation") {
      return handleCommonCreateRequest("user-badge-relations", variables);
    }
    if (resource === "products") {
      variables = {
        ...variables,
        privileges: "",
      };
      return handleCommonCreateRequest("products", variables);
    }
    if (resource === "prod-categories") {
      variables = { ...variables, privileges: "", simPrivileges: "" };
      return handleCommonCreateRequest("prod-categories", variables);
    }
    if (resource === "rooms") {
      variables = {
        ...variables,
        name: title,
        sourceType: parseInt(sourceType),
        status: 1,
        avatarUrl:
          Object.prototype.toString.call(avatarUrl) === "[object FileList]"
            ? ""
            : avatarUrl,
      };
      return handleCommonCreateRequest("rooms", variables);
    }
    if (resource === "role-badges-assignments")
      url = `${apiUrl}/role-badge-relation`;
    if (isFile(resource)) {
      return handleAudioUpload(variables);
    }
    if (resource === "room-member") {
      return handleCommonCreateRequest("room-members", variables);
    }
    if (
      resource === "product-badge-relation" ||
      resource === "product-room-relation"
    ) {
      return handleCommonCreateRequest(
        `${resource}s` as TableNameType,
        variables
      );
    }
    const { data } = await httpClient.post(url, variables);
    return {
      data,
    };
  },

  update: async ({ resource: origin, id: originId, variables }) => {
    const resource = resourceMap[origin] || origin;
    const id = originId.toString();
    const {
      badgeId,
      description,
      gte,
      name,
      crmId,
      privileges,
      simPrivileges,
      status,
      dtdValue,
      value,
    } = variables as Record<string, unknown>;
    const { config, playerParams, pusherParams, showName } =
      variables as Record<string, string>;
    const { vendorId, ...rest } = variables as Record<string, string>;
    if (isAnalyticsRequest(resource)) {
      const data = handleUpdateData(variables, SettingDebugInfo);
      const res = await handleSettingPatch(SettingDebugInfo.id, data);
      return res;
    }
    let url = `${apiUrl}/${resource}`;
    if (isTicket(resource)) {
      return handleCommonPatchRequest("tickets", id + "", variables);
    }
    if (resource === "symbols") url = `${apiUrl}/${resource}/forward`;
    if (isCommonReasons(resource)) {
      return handleCommonPatchRequest("common-reasons", id + "", variables);
    }
    if (resource === "quotestream-packages") {
      let newVariables = variables;
      if (name) {
        const key = (name as string).replace(/\s+/g, "_").toUpperCase();
        newVariables = { ...newVariables, key };
      }
      return handleCommonPatchRequest("quotestream-packages", id, newVariables);
    }
    if (isReportedMessage(resource)) {
      return handleCommonPatchRequest(
        "api/v2/reported-messages",
        id + "",
        variables,
        "message"
      );
    }
    if (isSettings(resource)) {
      const result = await handleCommonPatchRequest(
        "settings",
        id + "",
        variables
      );
      if (result && result.name === "scannerServer") {
        handleHpa(variables);
      }
      return;
    }
    if (resource === "products") {
      url = `${apiUrl}/product`;
      let newVariables = {};
      if (crmId) {
        newVariables = {
          admin: "true",
          crmId,
          description,
          privileges: privileges?.toString(),
        };
      } else {
        newVariables = {
          activeStatus: 1,
        };
      }

      return handleCommonPatchRequest("products", id as string, newVariables);
    }
    if (resource === "prod-categories") {
      let newVariables = {};
      if (crmId) {
        newVariables = {
          ...variables,
          admin: "true",
          privileges: privileges?.toString(),
          simPrivileges: simPrivileges?.toString(),
        };
      } else {
        newVariables = {
          activeStatus: status,
        };
      }

      return handleCommonPatchRequest("prod-categories", id + "", newVariables);
    }
    if (isReview(resource)) {
      return handleCommonPatchRequest(
        "uri-reviews",
        id + "",
        { status },
        "message"
      );
    }
    if (resource === "room-member") {
      return handleCommonPatchRequest("room-members", id as string, variables);
    }
    if (resource === "stream-vendors") {
      const isJSON = (str: any) => {
        if (typeof str == "string") {
          try {
            JSON.parse(str);
            return true;
          } catch (e) {
            return false;
          }
        } else {
          return false;
        }
      };
      let newVariables = {};
      if (config) {
        newVariables = {
          config: isJSON(config) ? JSON.parse(config) : config,
          name,
          playerParams: isJSON(playerParams)
            ? JSON.parse(playerParams)
            : playerParams,
          pusherParams: isJSON(pusherParams)
            ? JSON.parse(pusherParams)
            : pusherParams,
          showName,
        };
      } else {
        newVariables = {
          status,
        };
      }
      return handleCommonPatchRequest(
        "stream-vendors",
        id as string,
        newVariables
      );
    }
    if (resource === "rooms") {
      const newVariables = {
        ...variables,
        updatedBy: "Admin",
      };
      return handleCommonPatchRequest("rooms", id as string, newVariables);
    }
    if (resource === "user") {
      url = `${apiUrl}/v2/admin/${resource}/profile`;
    }
    if (resource === "role-badges-assignments") {
      return handleCommonPatchRequest(
        "role-badge-relations",
        id as string,
        variables
      );
    }
    if (isBadWordRequest(resource)) {
      return handleCommonPatchRequest(
        "bad-words",
        id as string,
        variables,
        "message"
      );
    }
    if (resource === "badge") {
      return handleCommonPatchRequest("badges", id as string, variables);
    }
    if (resource === "room") {
      return handleCommonPatchRequest("rooms", id as string, variables);
    }
    if (resource === "badge-type") {
      return handleCommonPatchRequest("badge-types", id as string, variables);
    }
    if (resource === "user-badge-relation") {
      return handleCommonPatchRequest(
        "user-badge-relations",
        id as string,
        variables
      );
    }
    if (resource === "followers-settings") {
      let newVariables = {
        admin: "true",
        badgeId,
        description,
        gte,
        name,
      };
      return handleCommonPatchRequest(
        "badge-rules",
        id as string,
        newVariables
      );
    }
    if (
      resource === "setting" &&
      (name === "scannerAlertSound" ||
        name === "scannerHaltSound" ||
        name === "scannerRunningUpSound")
    ) {
      let requestUrl = `${url}?id=${id}`;
      let newVariables = {
        value,
      };
      const { data } = await httpClient.patch(requestUrl, newVariables);
      window.history.go();
      return {
        data,
      };
    }
    let requestUrl = `${url}?id=${id}`;
    const { data } = await httpClient.patch(requestUrl, variables);
    return {
      data,
    };
  },

  getOne: async ({ resource: origin, id: originId }) => {
    const resource = resourceMap[origin] || origin;
    const id = originId.toString();
    let url = `${apiUrl}/${resource}`;
    if (resource === "symbols") url = `${apiUrl}/${resource}/forward`;

    let requestUrl = `${url}?id=${id}`;
    if (resource === "quotestream-packages") {
      const data = await handleCommonGetRequest("quotestream-packages", id);
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "scanner-config-changes") {
      const { data } = await httpClient.get(
        `${apiUrl}/v1/scanner/config-changes?limit=100&isAsc=true&skip=0`
      );
      return { data: data.data.find((item: any) => item.id === id) };
    }
    if (resource === "reported-messages-statistics")
      requestUrl = `${url}?userId=${id}`;
    if (resource === "user") {
      const data = await handleCommonGetRequest("users", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "user-related-data") {
      const data = await handleCommonGetRequest(
        "user-related-data",
        id.toString()
      );
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "badge") {
      const data = await handleCommonGetRequest("badges", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "stream-vendors") {
      const data = await handleCommonGetRequest(
        "stream-vendors",
        id.toString()
      );
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "badge-type") {
      const data = await handleCommonGetRequest("badge-types", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "followers-settings") {
      const data = await handleCommonGetRequest("badge-rules", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "role-badges-assignments") {
      const data = await handleCommonGetRequest(
        "role-badge-relations",
        id.toString()
      );
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "rooms") {
      const data = await handleCommonGetRequest("rooms", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "room-member") {
      const data = await handleCommonGetRequest("room-members", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "products") {
      const data = await handleCommonGetRequest("products", id.toString());
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    if (resource === "prod-categories") {
      const data = await handleCommonGetRequest(
        "prod-categories",
        id.toString()
      );
      return {
        data: data?.data?.[0] ?? data,
      };
    }
    const authToken = window.sessionStorage.getItem("authorization");
    httpClient.defaults.headers = {
      ...httpClient.defaults.headers,
      ...{ Authorization: "Bearer " + authToken },
    };
    const { data } = await httpClient.get(requestUrl);
    return {
      data: data?.data?.[0] ?? data,
    };
  },

  deleteOne: async ({ resource: origin, id: originId }) => {
    const resource = resourceMap[origin] || origin;
    const id = originId.toString();
    if (isAnalyticsRequest(resource)) {
      const data = handleDeleteData(id, SettingDebugInfo);
      const res = await handleSettingPatch(SettingDebugInfo.id, data);
      return res;
    }
    let requestUrl = `${apiUrl}/${resource}/?id=${id}`;
    if (resource === "symbols")
      requestUrl = `${apiUrl}/${resource}/forward/${id}`;

    if (CommonDeleteResources.includes(resource)) {
      return handleCommonDeleteRequest(resource, id);
    }

    if (isBadWordRequest(resource)) {
      return handleCommonDeleteRequest("bad-words", id as string, "message");
    }
    if (isCommonReasons(resource)) {
      return handleCommonDeleteRequest("common-reasons", id as string);
    }
    if (resource === "room-member") {
      return handleCommonDeleteRequest("room-members", id as string);
    }
    if (resource === "product-room-relation") {
      return handleCommonDeleteRequest("product-room-relations", id as string);
    }
    if (resource === "product-badge-relation") {
      return handleCommonDeleteRequest("product-badge-relations", id as string);
    }
    if (resource === "followers-settings") {
      return handleCommonDeleteRequest("badge-rules", id as string);
    }
    if (resource === "role-badges-assignments") {
      return handleCommonDeleteRequest("role-badge-relations", id as string);
    }
    if (resource === "user-badge-relation") {
      return handleCommonDeleteRequest("user-badge-relations", id as string);
    }
    if (resource === "user") {
      requestUrl = `${apiUrl}/v2/admin/user-related-data?id=${id}&infoStr={}`;
    }
    if (isReportedMessage(resource)) {
      return handleCommonDeleteRequest(
        "api/v2/reported-messages",
        id + "",
        "message"
      );
    }
    const { data } = await httpClient.delete(requestUrl);
    return {
      data,
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {
    let requestUrl = `${url}?`;

    if (sorters) {
      // const generatedSort = generateSort(sorters);
      // if (generatedSort) {
      //   const { _sort, _order } = generatedSort;
      //   const sortQuery = {
      //     _sort: _sort.join(","),
      //     _order: _order.join(","),
      //   };
      //   requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      // }
    }

    // if (filters) {
    //   const filterQuery = generateFilter(filters);
    //   requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    // }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    const authToken = window.sessionStorage.getItem("authorization");
    httpClient.defaults.headers = {
      ...httpClient.defaults.headers,
      ...{ Authorization: "Bearer " + authToken },
    };

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload);
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url, {
          data: payload,
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
});
